import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useCategorizationLearning from '@/hooks/useCategorizationLearning';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeCategoriesService from '@/services/financeCategories';
import financeCategorizationEventsService from '@/services/financeCategorizationEvents';
import financeRulesService from '@/services/financeRules';
import financeSubscriptionsService from '@/services/financeSubscriptions';
import financeTransactionsService from '@/services/financeTransactions';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import {
  FxTransaction,
  FxVendor,
  FxCategory,
  FxSubscription,
  FxCategorizationRule,
} from '@/types/api/finance';
import FinanceXTransactionsComponent from './FinanceXTransactions.component';

const FinanceXTransactionsContainer = function (): JSX.Element {
  const queryClient = useQueryClient();
  const orgId = getActiveOrgId();
  const [showForm, setShowForm] = useState(false);
  const [editingTx, setEditingTx] = useState<FxTransaction | null>(null);
  const [showSubForm, setShowSubForm] = useState(false);
  const [subFromTx, setSubFromTx] = useState<FxTransaction | null>(null);

  const txResponse = useApiFetch<{ data: FxTransaction[] }, void>(
    [FetchApiIds.FX_TRANSACTIONS, orgId],
    async () => financeTransactionsService.getAll({ page: 1, take: 1000 }),
    { retry: false }
  );

  const vendorsResponse = useApiFetch<{ data: FxVendor[] }, void>(
    [FetchApiIds.FX_VENDORS, orgId],
    async () => financeVendorsService.getAll({ page: 1, take: 200 }),
    { retry: false }
  );

  const categoriesResponse = useApiFetch<FxCategory[], void>(
    [FetchApiIds.FX_CATEGORIES, orgId],
    async () => financeCategoriesService.getAll(),
    { retry: false }
  );

  const subsResponse = useApiFetch<{ data: FxSubscription[] }, void>(
    [FetchApiIds.FX_SUBSCRIPTIONS, orgId],
    async () => financeSubscriptionsService.getAll({ page: 1, take: 200 }),
    { retry: false }
  );

  const transactions = useMemo(() => (txResponse.data as any)?.data ?? [], [txResponse.data]);

  const vendors = useMemo(() => (vendorsResponse.data as any)?.data ?? [], [vendorsResponse.data]);

  const categories = useMemo(
    () => (categoriesResponse.data as FxCategory[]) ?? [],
    [categoriesResponse.data]
  );

  const subscriptions = useMemo(() => (subsResponse.data as any)?.data ?? [], [subsResponse.data]);

  const rulesResponse = useApiFetch<FxCategorizationRule[], void>(
    ['fx-active-rules', orgId],
    async () => financeRulesService.getAllActive(),
    { retry: false }
  );

  const rules = useMemo(
    () => (rulesResponse.data as FxCategorizationRule[]) ?? [],
    [rulesResponse.data]
  );

  const { autoLearnIfNeeded } = useCategorizationLearning({ vendors, categories, rules });

  // Build a set of vendorIds that have active subscriptions
  const subscribedVendorIds = useMemo(
    () =>
      new Set<string>(
        subscriptions
          .filter((s: FxSubscription) => s.status === 'active' || s.status === 'trial')
          .map((s: FxSubscription) => s.vendorId)
      ),
    [subscriptions]
  );

  const createMutation = useApiSubmit<FxTransaction, any>(
    (payload) => financeTransactionsService.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
        setShowForm(false);
      },
    }
  );

  const updateMutation = useApiSubmit<FxTransaction, any>(
    (payload) => financeTransactionsService.update(payload.id, payload),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
        // Log categorization event when vendor or category changed
        if (editingTx) {
          const vendorChanged =
            variables.vendorId !== undefined && variables.vendorId !== editingTx.vendorId;
          const categoryChanged =
            variables.categoryId !== undefined && variables.categoryId !== editingTx.categoryId;
          if (vendorChanged || categoryChanged) {
            financeCategorizationEventsService
              .logEvent({
                transactionId: editingTx.id,
                eventType: 'manual_edit',
                prevVendorId: editingTx.vendorId,
                prevCategoryId: editingTx.categoryId,
                prevMatchSource: editingTx.matchSource,
                newVendorId: variables.vendorId ?? editingTx.vendorId,
                newCategoryId: variables.categoryId ?? editingTx.categoryId,
                ruleId: editingTx.matchedRuleId,
                aiConfidence: editingTx.aiConfidence,
                description: editingTx.description,
                amount: editingTx.amount,
              })
              .then(() => autoLearnIfNeeded())
              .catch(() => {});
            // Increment override count if overriding a rule match
            if (editingTx.matchedRuleId) {
              financeRulesService.incrementOverrideCount(editingTx.matchedRuleId).catch(() => {});
            }
          }
        }
        setShowForm(false);
        setEditingTx(null);
      },
    }
  );

  const deleteMutation = useApiSubmit<void, any>(
    (payload) => financeTransactionsService.delete(payload.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
      },
    }
  );

  const handleSubmit = useCallback(
    (data: any) => {
      if (editingTx) {
        updateMutation.mutate({ id: editingTx.id, ...data });
      } else {
        createMutation.mutate(data);
      }
    },
    [editingTx, createMutation, updateMutation]
  );

  const handleEdit = useCallback((tx: FxTransaction) => {
    setEditingTx(tx);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    (tx: FxTransaction) => {
      if (!window.confirm('Delete this transaction?')) return;
      deleteMutation.mutate({ id: tx.id });
    },
    [deleteMutation]
  );

  const handleBulkDelete = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      await financeTransactionsService.bulkDelete(ids);
      queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
    },
    [queryClient]
  );

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingTx(null);
  }, []);

  const [isSubSubmitting, setIsSubSubmitting] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  const handleCreateSubscription = useCallback((tx: FxTransaction) => {
    setSubFromTx(tx);
    setShowSubForm(true);
    setSubError(null);
  }, []);

  const handleSubSubmit = useCallback(
    async (data: any) => {
      setIsSubSubmitting(true);
      setSubError(null);
      try {
        let { vendorId } = data;

        // Handle new vendor creation
        if (vendorId === '__new__' && data.newVendorName) {
          const nameLower = data.newVendorName.trim().toLowerCase();
          const existing = vendors.find(
            (v: FxVendor) =>
              v.name.toLowerCase() === nameLower || v.displayName?.toLowerCase() === nameLower
          );
          if (existing) {
            vendorId = existing.id;
          } else {
            const created = await financeVendorsService.create({ name: data.newVendorName.trim() });
            vendorId = (created.data as any).id;
            queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
          }
        } else if (vendorId === '__new__') {
          setSubError('Please enter a vendor name.');
          setIsSubSubmitting(false);
          return;
        }

        // Check for duplicate subscription (same vendor + similar name)
        const duplicate = subscriptions.find(
          (s: FxSubscription) =>
            s.vendorId === vendorId &&
            s.name.toLowerCase() === data.name.trim().toLowerCase() &&
            (s.status === 'active' || s.status === 'trial')
        );
        if (duplicate) {
          setSubError(`A subscription "${duplicate.name}" already exists for this vendor.`);
          setIsSubSubmitting(false);
          return;
        }

        // Build clean payload
        const subPayload: Record<string, unknown> = {
          vendorId,
          name: data.name,
          amount: Number(data.amount),
          billingCycle: data.billingCycle,
          startDate: data.startDate,
        };
        if (data.currency) subPayload.currency = data.currency;
        if (data.nextBilling) subPayload.nextBilling = data.nextBilling;
        if (data.seats) subPayload.seats = Number(data.seats);
        if (data.status) subPayload.status = data.status;
        if (data.owner) subPayload.owner = data.owner;
        if (data.notes) subPayload.notes = data.notes;

        const newSub = await financeSubscriptionsService.create(subPayload as any);
        const newSubId = (newSub.data as any).id;

        // Update the source transaction to link to this vendor and subscription
        if (subFromTx) {
          const txUpdates: Record<string, unknown> = {};
          if (!subFromTx.vendorId && vendorId) txUpdates.vendorId = vendorId;
          if (!subFromTx.subscriptionId) txUpdates.subscriptionId = newSubId;
          if (Object.keys(txUpdates).length > 0) {
            await financeTransactionsService.update(subFromTx.id, txUpdates);
          }
        }

        queryClient.invalidateQueries(FetchApiIds.FX_SUBSCRIPTIONS);
        queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
        queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
        setShowSubForm(false);
        setSubFromTx(null);
      } catch (err: any) {
        setSubError(err.message || 'Failed to create subscription.');
      } finally {
        setIsSubSubmitting(false);
      }
    },
    [vendors, subscriptions, queryClient]
  );

  const handleCloseSubForm = useCallback(() => {
    setShowSubForm(false);
    setSubFromTx(null);
  }, []);

  return (
    <FinanceXTransactionsComponent
      transactions={transactions}
      vendors={vendors}
      categories={categories}
      isLoading={txResponse.isLoading}
      showForm={showForm}
      editingTx={editingTx}
      onNewTransaction={() => setShowForm(true)}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onBulkDelete={handleBulkDelete}
      onSubmit={handleSubmit}
      onCloseForm={handleCloseForm}
      isSubmitting={createMutation.isLoading || updateMutation.isLoading}
      subscribedVendorIds={subscribedVendorIds}
      showSubForm={showSubForm}
      subFromTx={subFromTx}
      onCreateSubscription={handleCreateSubscription}
      onSubSubmit={handleSubSubmit}
      onCloseSubForm={handleCloseSubForm}
      isSubSubmitting={isSubSubmitting}
      subError={subError}
    />
  );
};

export default FinanceXTransactionsContainer;
